// Timezone helpers for the Live Class feature (and anything else that ever
// needs "admin picks a local date/time in some zone" <-> "everyone else
// sees their own correct local time"). Deliberately dependency-free — no
// date-fns/luxon/moment, since the project doesn't already use one and
// native Intl covers all of this.

// Only used if the browser doesn't support Intl.supportedValuesOf (older
// Safari/Firefox) — a real dropdown still works, just a shorter list.
const FALLBACK_TIMEZONES = [
    "UTC",
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Sao_Paulo",
    "Europe/London", "Europe/Paris", "Europe/Istanbul",
    "Africa/Cairo", "Africa/Lagos", "Africa/Johannesburg",
    "Asia/Dubai", "Asia/Riyadh", "Asia/Karachi", "Asia/Kolkata", "Asia/Dhaka",
    "Asia/Bangkok", "Asia/Jakarta", "Asia/Kuala_Lumpur", "Asia/Singapore",
    "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul",
    "Australia/Sydney", "Australia/Perth", "Pacific/Auckland",
];

// Real letter abbreviations for zones Intl's own `timeZoneName: "short"`
// doesn't give one for (it only really covers North America before
// falling back to a bare "GMT+5:30" offset) — filled in for the zones
// this app's audience is actually likely to be in. Anything not listed
// here just falls back to whatever Intl produces, offset included.
const TZ_ABBR = {
    "Asia/Kolkata": "IST", "Asia/Calcutta": "IST",
    "Asia/Karachi": "PKT",
    "Asia/Dhaka": "BST",
    "Asia/Dubai": "GST",
    // "KSA" here specifically (not the generic "AST" the other Gulf zones
    // below get) — that's how Saudi Arabia's own time is commonly labeled
    // in everyday use (Google's own time-conversion answer says "KSA" too),
    // requested explicitly for the Live Class card's Saudi Arabia line.
    "Asia/Riyadh": "KSA",
    "Asia/Qatar": "AST",
    "Asia/Kuwait": "AST",
    "Asia/Bahrain": "AST",
    "Asia/Baghdad": "AST",
    "Asia/Jakarta": "WIB",
    "Asia/Kuala_Lumpur": "MYT",
    "Asia/Singapore": "SGT",
    "Asia/Manila": "PHT",
    "Asia/Istanbul": "TRT",
    "Europe/Istanbul": "TRT",
    "Africa/Cairo": "EET",
    "Africa/Lagos": "WAT",
    "Africa/Nairobi": "EAT",
    "Africa/Johannesburg": "SAST",
};

export function getSupportedTimezones() {
    let zones;
    try {
        zones = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : FALLBACK_TIMEZONES;
    } catch {
        zones = FALLBACK_TIMEZONES;
    }
    if (!zones || !zones.length) zones = FALLBACK_TIMEZONES;
    return ["UTC", ...zones.filter((z) => z !== "UTC")];
}

// Combines a plain <input type="date"> + <input type="time"> pair (each
// just a local wall-clock string, no zone info of its own) with the
// timezone the admin picked, into the single UTC instant that actually
// gets stored. Classic "guess as UTC, see what that guess looks like in
// the target zone, correct by the difference" trick — the only way to do
// this correctly with plain Intl (no Temporal support yet), and it
// handles DST correctly for anything outside the DST-transition instant
// itself.
export function zonedTimeToUtcIso(dateStr, timeStr, timeZone) {
    if (!dateStr || !timeStr || !timeZone) return "";
    const [y, m, d] = dateStr.split("-").map(Number);
    const [hh, mm] = timeStr.split(":").map(Number);
    if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return "";

    const guessUtcMs = Date.UTC(y, m - 1, d, hh, mm, 0);
    let parts;
    try {
        parts = Object.fromEntries(
            new Intl.DateTimeFormat("en-US", {
                timeZone, hour12: false,
                year: "numeric", month: "2-digit", day: "2-digit",
                hour: "2-digit", minute: "2-digit", second: "2-digit",
            }).formatToParts(new Date(guessUtcMs)).map((p) => [p.type, p.value])
        );
    } catch {
        return new Date(guessUtcMs).toISOString(); // bad zone name — best effort, treat as UTC
    }
    const shownAsUtcMs = Date.UTC(
        Number(parts.year), Number(parts.month) - 1, Number(parts.day),
        Number(parts.hour) === 24 ? 0 : Number(parts.hour), Number(parts.minute), Number(parts.second)
    );
    const correctedMs = guessUtcMs + (guessUtcMs - shownAsUtcMs);
    return new Date(correctedMs).toISOString();
}

// The inverse — decoding a stored UTC instant back into a {date, time}
// pair for the admin's OWN re-edit, in the zone it was ORIGINALLY set in
// (scheduled_timezone), not the zone the admin's browser happens to be in
// right now. This is the piece that makes reopening the form show back
// exactly what was saved instead of a re-interpretation.
export function utcIsoToZonedParts(isoUtc, timeZone) {
    if (!isoUtc) return { date: "", time: "" };
    const d = new Date(isoUtc);
    if (Number.isNaN(d.getTime())) return { date: "", time: "" };
    try {
        const parts = Object.fromEntries(
            new Intl.DateTimeFormat("en-CA", {
                timeZone: timeZone || "UTC", hour12: false,
                year: "numeric", month: "2-digit", day: "2-digit",
                hour: "2-digit", minute: "2-digit",
            }).formatToParts(d).map((p) => [p.type, p.value])
        );
        const hh = parts.hour === "24" ? "00" : parts.hour;
        return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${hh}:${parts.minute}` };
    } catch {
        return { date: "", time: "" };
    }
}

// Renders a stored UTC instant in ONE fixed zone/locale, with a real
// timezone abbreviation appended so it reads as an unambiguous instant
// ("7:00 PM IST") rather than a bare, ambiguous-looking time.
//
// `showDate: false` drops the weekday/month/day and returns just the time
// + abbreviation ("7:00 PM IST") — for LiveClassCard showing the same
// instant in a couple of extra zones (Malaysia, KSA) alongside the main
// IST line, where repeating the same date three times over would be
// redundant.
export function formatInZone(isoUtc, timeZone, { showDate = true } = {}) {
    if (!isoUtc || !timeZone) return "";
    const d = new Date(isoUtc);
    if (Number.isNaN(d.getTime())) return "";
    let base;
    try {
        base = new Intl.DateTimeFormat(undefined, {
            ...(showDate ? { weekday: "short", month: "short", day: "numeric" } : {}),
            hour: "numeric", minute: "2-digit",
            timeZone,
        }).format(d);
    } catch {
        base = d.toLocaleString();
    }
    let abbr = TZ_ABBR[timeZone];
    if (!abbr) {
        try {
            const withZone = new Intl.DateTimeFormat(undefined, { timeZoneName: "short", hour: "numeric", timeZone }).formatToParts(d);
            abbr = (withZone.find((p) => p.type === "timeZoneName") || {}).value || "";
        } catch {
            abbr = "";
        }
    }
    return abbr ? `${base} ${abbr}` : base;
}

// Renders a stored UTC instant in the VIEWER's own local zone/locale —
// kept around for anything that ever wants real per-visitor conversion
// again, even though LiveClassCard itself now always shows IST (see
// formatInZone above and its "Asia/Kolkata" call site).
export function formatInViewerLocal(isoUtc) {
    let timeZone = "UTC";
    try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { /* keep UTC */ }
    return formatInZone(isoUtc, timeZone);
}

// A plain <input type="time"> hands its AM/PM toggle entirely to the
// browser's own OS-styled widget — on some platforms that's a small
// segment you click and scroll/type into, easy to miss it's interactive
// at all, not an obvious dropdown. These convert the underlying 24-hour
// "HH:MM" state (what zonedTimeToUtcIso/utcIsoToZonedParts already speak)
// to/from an explicit {hour: "01"-"12", minute: "00"-"59", meridiem:
// "AM"|"PM"} triple for a real <select>-based picker instead.
export function to12Hour(hhmm) {
    if (!hhmm) return { hour: "07", minute: "00", meridiem: "PM" };
    const [hStr, mStr] = hhmm.split(":");
    let h = parseInt(hStr, 10);
    if (Number.isNaN(h)) h = 19;
    const meridiem = h >= 12 ? "PM" : "AM";
    h = h % 12;
    if (h === 0) h = 12;
    return { hour: String(h).padStart(2, "0"), minute: (mStr || "00").padStart(2, "0"), meridiem };
}

export function to24Hour(hour12, minute, meridiem) {
    let h = parseInt(hour12, 10) % 12;
    if (meridiem === "PM") h += 12;
    return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

// Defense-in-depth URL check for the frontend — the backend already
// validates this on save, but a config record saved before that
// validation existed shouldn't get a free pass into a clickable link.
export function isSafeHttpsUrl(url) {
    if (!url) return false;
    try {
        const parsed = new URL(url);
        return parsed.protocol === "https:" && !!parsed.hostname;
    } catch {
        return false;
    }
}
